@if (!isTranslationToolEngaged) {
  <div
    data-automation-id="annotation-context-menu"
    role="dialog"
    [attr.aria-label]="'annotation.annotation_toolbar' | translate"
    cdkTrapFocus
    [cdkTrapFocusAutoCapture]="true"
    rdrxHideOnEscape
    >
    <!--
    TOP/MAIN CONTEXT MENU ACTIONS: Highlight, Placemark, Note, Readspeaker, AI Assist, Translation Tool, Close
    - Each action has an icon button and an optional underline indicator to show if it's active (e.g. if the user has already added a placemark, the placemark button will be underlined)
    - If the action has additional options (e.g. highlight color and shape options), those options will be shown in a secondary pane above the main actions when the user clicks the main action button. The main action button will also have an aria-pressed state to indicate that the options pane is open.
    -->
    @if (!onlyReadspeakerEnabled) {
      <ul class="action-container">
        @if (config.highlights) {
          <li class="underline-container">
            <button
              mhe-button
              [purpose]="ButtonPurpose.Icon"
              class="action"
              id="add-annotation-highlight"
              data-automation-id="add-annotation-highlight"
              (click)="highlight()"
              [mheTooltip]="'context.add_annotation' | translate"
              placement="bottom"
              [attr.aria-pressed]="isHighlightPressed('default') | async"
              >
              <i class="dpg-icon dpg-icon-highlighter"></i>
            </button>
            <div [ngClass]="activeClass$ | async"></div>
          </li>
        }
        @if (config.placemarks) {
          <li class="underline-container">
            <button
              mhe-button
              [purpose]="ButtonPurpose.Icon"
              class="action"
              data-automation-id="placemark-toggle"
              (click)="placemark()"
        [mheTooltip]="
          (store.placemarkText$ | async)?.length > 0
            ? ('context.remove_placemark' | translate)
            : ('context.add_placemark' | translate)"
              placement="bottom"
              [attr.aria-pressed]="isPlacemarkPressed() | async"
              >
              <i class="placemark-icon"></i>
            </button>
            <div [class.underline-container-default]="(store.placemarkText$ | async)?.length > 0"></div>
          </li>
        }
        @if (config.notes) {
          <li class="underline-container">
            <button
              mhe-button
              [purpose]="ButtonPurpose.Icon"
              class="action"
              data-automation-id="add-note"
              (click)="store.showOptionsPane('note') ; focusTextArea()"
        [mheTooltip]="
          (store.note$ | async)?.length > 0
            ? ('context.edit_note' | translate)
            : ('context.add_note' | translate)
        "
              placement="bottom"
              [attr.aria-pressed]="(store.visibleOptionsPane$ | async) === 'note'"
              >
              <i class="ahe-icon dpg-icon-notepad"></i>
            </button>
            <div
        [class.underline-container-default]="
          (store.note$ | async)?.length > 0 || (store.visibleOptionsPane$ | async) === 'note'
        "
            ></div>
          </li>
        }
        @if (config.readspeaker && (ttsHidden$ | async) === false) {
          <li class="readspeaker-icon">
            <button
              mhe-button
              [purpose]="ButtonPurpose.Icon"
              class="action"
              data-automation-id="read-selected-text"
              (click)="onLoadAudioText()"
              [mheTooltip]="'readspeaker.read_selected' | translate"
              placement="bottom"
              [attr.aria-pressed]="isReadspeakerPressed() | async"
              >
              <i class="dpg-icon dpg-icon-audioon"></i>
            </button>
          </li>
        }
        @if (config.isAiAssistOffered) {
          <li class="underline-container ai-assist" >
            <button
              mhe-button
              [purpose]="ButtonPurpose.Icon"
              class="action"
              data-automation-id="ai-assist"
              (click)="onClickAiAssist()"
              [mheTooltip]="'ai_tools.ai_assist.button' | translate"
              placement="bottom"
              [attr.aria-pressed]="isAiAssistPressed() | async"
              >
              <i class="ai-assist ai-assist-icon"></i>
            </button>
            <!-- only show BETA badge if in FA4 context -->
            @if ((config$ | async)?.topicsEndpoint) {
              <span class="badge">BETA</span>
            }
          </li>
        }
        @if (config.isTranslationOffered) {
          <li class="underline-container">
            <button
              #translationButton
              mhe-button
              [purpose]="ButtonPurpose.Icon"
              class="action"
              data-automation-id="translation-tool"
              [mheTooltip]="'translation_tool.button' | translate"
              placement="bottom"
              (click)="openTranslationTool()"
              [attr.aria-pressed]="isTranslationToolEngaged"
              >
              <i class="translation translation-icon" aria-hidden="true"></i>
            </button>
          </li>
        }
        <li>
          <button
            mhe-button
            [purpose]="ButtonPurpose.Icon"
            class="action"
            data-automation-id="context-menu-close"
            (click)="close()"
            [mheTooltip]="'navigation.close' | translate"
            placement="bottom"
            cdkFocusInitial
            >
            <i class="dpg-icon dpg-icon-system-close"></i>
          </button>
        </li>
      </ul>
    }
    <!--
    HIGHLIGHT COLOR AND SHAPE OPTIONS PANE (appears when user clicks the highlight button)
    - Shows highlight shape options (highlight, underline, box, circle) and color options (default and any custom colors provided via config)
    - The currently selected shape and color will be indicated with an underline and a border around the color option, respectively
    - There is also a trash icon to remove the highlight, which will set the highlight color to 'none' in the store
    -->
    @if ((store.visibleOptionsPane$ | async) === 'color') {
      <div class="highlight-selection-pane" data-automation-id="highlight-selection-pane">
        <!--
        HIGHLIGHT SHAPE OPTIONS (highlight, underline, box, circle)
        - Each option is represented by an icon button. The user can select a shape to apply that shape to their highlight annotation.
        - The currently selected shape will have an underline indicator (the color of the underline will match the selected highlight color)
        -->
        <ul class="highlight-selection-pane__header">
          @if (config.highlights) {
            <li>
              <button
                mhe-button
                [purpose]="ButtonPurpose.Icon"
                class="action"
                id="default-annotation-highlight"
                data-automation-id="default-annotation-highlight"
                (click)="highlight('default', true)"
                [mheTooltip]="'context.add_highlight' | translate"
                placement="bottom"
                [attr.aria-pressed]="isHighlightPressed('default') | async"
                >
                <img
                  id='highlight-icon'
                  data-automation-id="highlight-icon"
                  src="/assets/icons/reader/highlight.svg"
                  alt="Highlight Icon"
                  width="20px">
              </button>
              @if (isHighlightPressed('default') | async) {
                <div [ngClass]="defaultClass$ | async"></div>
              } @else {
                <div style="height: 4px; margin-top: 6px;"></div>
              }
            </li>
          }
          @if (config.highlights) {
            <li>
              <button
                mhe-button
                [purpose]="ButtonPurpose.Icon"
                class="action"
                id="add-annotation-underline"
                data-automation-id="add-annotation-underline"
                (click)="highlight('underline', true)"
                [mheTooltip]="'context.add_underline' | translate"
                placement="bottom"
                [attr.aria-pressed]="isHighlightPressed('underline') | async"
                >
                <i class="dpg-icon dpg-icon-keyboard-letters-underline"></i>
              </button>
              @if (isHighlightPressed('underline') | async) {
                <div [ngClass]="underlineClass$ | async"></div>
              } @else {
                <div style="height: 4px; margin-top: 6px;"></div>
              }
            </li>
          }
          @if (config.highlights) {
            <li>
              <button
                mhe-button
                [purpose]="ButtonPurpose.Icon"
                class="action"
                id="add-annotation-box"
                data-automation-id="add-annotation-box"
                (click)="highlight('box', true)"
                [mheTooltip]="'context.add_box' | translate"
                placement="bottom"
                [attr.aria-pressed]="isHighlightPressed('box') | async"
                >
                <i class="dpg-icon dpg-icon-keyboard-letters-rectangle"></i>
              </button>
              @if (isHighlightPressed('box') | async) {
                <div [ngClass]="boxClass$ | async"></div>
              } @else {
                <div style="height: 4px; margin-top: 6px;"></div>
              }
            </li>
          }
          @if (config.highlights) {
            <li>
              <button
                mhe-button
                [purpose]="ButtonPurpose.Icon"
                class="action"
                id="add-annotation-circle"
                data-automation-id="add-annotation-circle"
                (click)="highlight('circle', true)"
                [mheTooltip]="'context.add_circle' | translate"
                placement="bottom"
                [attr.aria-pressed]="isHighlightPressed('circle') | async"
                >
                <i class="dpg-icon dpg-icon-keyboard-letters-rectangle-rounded"></i>
              </button>
              @if (isHighlightPressed('circle') | async) {
                <div [ngClass]="circleClass$ | async"></div>
              } @else {
                <div style="height: 4px; margin-top: 6px;"></div>
              }
            </li>
          }
          @if (config.highlights) {
            <li class="trash-container">
              <button
                mhe-button
                [purpose]="ButtonPurpose.Icon"
                class="action"
                data-automation-id="remove-annotation"
                (click)="store.setHighlightColor('none')"
                [mheTooltip]="'context.trash_annotation' | translate"
                placement="bottom"
                [attr.aria-label]="'context.trash_annotation' | translate"
                >
                <img
                  id='trash-icon'
                  data-automation-id="trash-icon"
                  src="/assets/icons/reader/trash.svg"
                  alt="Trash Icon"
                  width="13px">
              </button>
              <ng-container *ngTemplateOutlet="colorSelectionPlaceholder"></ng-container>
            </li>
          }
        </ul>
        <!--
        HIGHLIGHT COLOR OPTIONS (default color and any custom colors provided via config)
        - Each option is represented by a button with a color icon. The user can select a color to apply that color to their highlight annotation.
        - The currently selected color will have a border around the color icon
        -->
        <ul class="highlight-selection-pane__colors">
          @for (colorOptionWithTooltip of colorOptionsWithTooltip$ | async; track colorOptionWithTooltip) {
            <li>
              <button
                [attr.data-automation-id]="'annotation-color-option-' + colorOptionWithTooltip.highlightColor"
                mhe-button
                [id]="colorOptionWithTooltip.highlightColor+'-button-icon'"
                [purpose]="ButtonPurpose.Icon"
                class="color-button"
                (click)="store.setHighlightColor(colorOptionWithTooltip.highlightColor)"
                [mheTooltip]="colorOptionWithTooltip.tooltip"
                [attr.aria-pressed]="(store.highlightColor$ | async) === colorOptionWithTooltip.highlightColor"
                >
                <rdrx-color-icon
                  [id]="colorOptionWithTooltip.highlightColor+'-icon'"
                  [color]="colorOptionWithTooltip.highlightColor"
                  [selected]="(store.highlightColor$ | async) === colorOptionWithTooltip.highlightColor"
                ></rdrx-color-icon>
              </button>
            </li>
          }
        </ul>
        <!--
        QUICK ANNOTATION OPTION (only appears on non-touch devices)
        - This is a button that allows the user to quickly add an annotation without having to select a shape or color. It will apply the default highlight color and shape to the annotation.
        -->
        @if (!isTouchScreen) {
          <div class="highlight-selection-pane__quick-annotation">
            <rdrx-quick-annotation></rdrx-quick-annotation>
          </div>
        }
      </div>
    }
    <!--
    READSPEAKER AUDIO CONTROLS PANE (appears when user clicks the readspeaker button)
    - Shows audio controls for the Readspeaker TTS audio, allowing the user to play/pause, adjust volume, and close the audio controls pane. The audio will be associated with the selected text annotation.
    -->
    @if ((store.visibleOptionsPane$ | async) === 'readspeaker' && (ttsHidden$ | async) === false) {
      <div
        [class.tts-audio-controls-pane]="!onlyReadspeakerEnabled"
        >
        @if (showLoading$ | async) {
          <div class="tts-spinner-container">
            <mat-progress-spinner
              class="rdrx-spinner annotation-toolbar-tts-spinner"
              data-automation-id="annotation-toolbar-tts-spinner"
              [mode]="'indeterminate'"
              [diameter]="30"
              [attr.aria-label]="'readspeaker.buffering' | translate"
            ></mat-progress-spinner>
          </div>
        } @else {
          <rdrx-audio-controls
            class="annotation-toolbar-tts-audio-controls"
            data-automation-id="annotation-toolbar-tts-audio-controls"
            [audioContext]="TTSAudioContext.HIGHLIGHT"
            [showClose]="onlyReadspeakerEnabled"
            (closeEvent)="close()"
            >
          </rdrx-audio-controls>
        }
      </div>
    }
    <!--
    NOTE INPUT PANE (appears when user clicks the note button)
    - Shows a textarea input for the user to add or edit a note associated with the selected text annotation. The user can also see a character counter indicating how many characters they have entered out of the maximum allowed.
    -->
    @if ((store.visibleOptionsPane$ | async) === 'note') {
      <div class="note-pane">
        <mhe-textarea
          #mheTextArea
          class="note-input"
          data-automation-id="note-input"
          [value]="store.note$ | async"
          (inputChange)="noteChanged($event)"
          [maxlength]="store.noteMaxCharacters$ | async"
          [rows]="undefined"
        ></mhe-textarea>
        <div class="character-counter" data-automation-id="note-character-counter">
          {{ (store.note$ | async)?.length || '0' }} / {{ store.noteMaxCharacters$ | async }}
        </div>
      </div>
    }
  </div>
}

@if (isTranslationToolEngaged) {
  <rdrx-translation-tool-shell
    role="dialog"
    data-automation-id="translation-tool-dialog"
    cdkTrapFocus
    cdkTrapFocusAutoCapture
    rdrxHideOnEscape
    [selectedText]="annotation.text"
    (closeRequested)="disengageTranslationTool()"
  ></rdrx-translation-tool-shell>
}

<!-- Utils -->
<ng-template #colorSelectionPlaceholder>
  <div style="height: 4px; margin-top: 6px;"></div>
</ng-template>
